import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import CentroDeAyuda from './CentroDeAyuda';
import { useJsonAyuda } from './contenido';
import { normalizarCatalogo, tutorialParaRuta } from './catalogo';
import {
  contarSinLeer,
  leerVistas,
  marcarVistas,
  normalizarNovedades,
  novedadesVigentes,
} from './novedades';

// Una sola puerta para abrir el centro de ayuda desde cualquier pantalla (ícono del encabezado, botones "Ver
// tutorial", feed de novedades) y el estado compartido de las novedades (vigentes, sin leer, vistas), para que el
// feed del inicio, la insignia del encabezado y el historial siempre muestren lo mismo. El catálogo de tutoriales
// se descarga recién la primera vez que se abre la ayuda.
//
//   const { abrir, novedades } = useAyuda();
//   abrir();                          → lista de tutoriales
//   abrir({ vista: 'novedades' })     → el historial de novedades
//   abrir({ tutorialId: 'gantt' })    → directo a ese tutorial
//   abrir({ deEstaPantalla: true })   → el tutorial de la pantalla actual si lo hay (si no, la lista)
//   novedades.vigentes / .sinLeer / .vistas / .marcarLeidas(ids)
const SIN_NOVEDADES = {
  vigentes: [],
  publicaciones: [],
  sinLeer: 0,
  vistas: [],
  hoy: '',
  marcarLeidas: () => undefined,
};
const AyudaContext = createContext({
  abrir: () => undefined,
  cerrar: () => undefined,
  novedades: SIN_NOVEDADES,
});

export const useAyuda = () => useContext(AyudaContext);

export default function AyudaProvider({ children, base, hoy: hoyProp }) {
  const { pathname } = useLocation();
  const [estado, setEstado] = useState({
    abierto: false,
    tutorialId: null,
    deEstaPantalla: false,
    vista: 'tutoriales',
  });
  const [pedido, setPedido] = useState(false);
  const opcionesBase = base ? { base } : {};

  const datos = useJsonAyuda('catalogo.json', { activo: pedido, ...opcionesBase });
  const catalogo = useMemo(() => (datos ? normalizarCatalogo(datos) : null), [datos]);

  const datosNovedades = useJsonAyuda('novedades.json', { activo: true, ...opcionesBase });
  const publicaciones = useMemo(
    () => (datosNovedades ? normalizarNovedades(datosNovedades) : []),
    [datosNovedades]
  );
  const [vistas, setVistas] = useState(() => leerVistas());
  const hoy = useMemo(() => hoyProp ?? new Date().toISOString().slice(0, 10), [hoyProp]);

  const marcarLeidas = useCallback((ids) => {
    marcarVistas(ids);
    // Se actualiza en memoria aunque el almacenamiento falle, para que la pantalla refleje lo leído.
    setVistas((previas) => [...new Set([...previas, ...ids])]);
  }, []);

  const novedades = useMemo(
    () => ({
      publicaciones,
      vigentes: novedadesVigentes({ publicaciones, hoy }),
      sinLeer: contarSinLeer({ publicaciones, hoy, vistas }),
      vistas,
      hoy,
      marcarLeidas,
    }),
    [publicaciones, hoy, vistas, marcarLeidas]
  );

  const abrir = useCallback((opciones = {}) => {
    setPedido(true);
    setEstado({
      abierto: true,
      tutorialId: opciones.tutorialId ?? null,
      deEstaPantalla: Boolean(opciones.deEstaPantalla),
      vista: opciones.vista === 'novedades' ? 'novedades' : 'tutoriales',
    });
  }, []);
  const cerrar = useCallback(() => setEstado((e) => ({ ...e, abierto: false })), []);

  const tutorialId =
    estado.tutorialId ??
    (estado.deEstaPantalla && catalogo ? tutorialParaRuta(catalogo, pathname) : null);
  const valor = useMemo(() => ({ abrir, cerrar, novedades }), [abrir, cerrar, novedades]);

  return (
    <AyudaContext.Provider value={valor}>
      {children}
      <CentroDeAyuda
        open={estado.abierto}
        onClose={cerrar}
        catalogo={catalogo}
        tutorialId={tutorialId}
        vista={estado.vista}
        novedades={novedades}
        {...opcionesBase}
      />
    </AyudaContext.Provider>
  );
}
